import { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MutationCache, QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { UNSAFE_NavigationContext, UNSAFE_LocationContext, UNSAFE_RouteContext, NavigationType, matchRoutes, useLocation, useNavigate, useRoutes, type To, type Location } from 'react-router';
import type { ChatContext, TodayWorkspace, TodayWorkspaceItem } from '@norum/contracts';
import { WorkingRecordProvider } from '@/api/WorkingRecord';
import { appPath, post } from '@/api/client';
import { TodayCallScope } from '@/api/TodayContext';
import { MeContext, useMeOrThrow } from '@/app/MeContext';
import { EmbeddedFrame, PageHeader, useFrame } from '@/app/frame';
import { contextFor } from '@/chat/context';
import { BUSINESS_ROUTES } from './business-routes';
import { ManualAdd } from '@/directory/ManualAdd';
import { failureOf, said } from '@/api/errors';
import { localIsoDate } from './dates';
import { addDays, startOfDay } from './grid';
import { longDate, SPAN_LABEL, STARTED_BY, type ViewSpan } from './copy';
import { DayActions } from './DayActions';
import { DayRecap } from './Recap';
import { FreeHours } from './FreeHours';
import { useExperience } from '@/api/hooks';
import { Covering } from './Covering';
import { InlineForms } from '@/ui/InlineForms';
import { Select } from '@/ui/fields';

function workingHour(value: unknown, fallback: number): number {
  const time = typeof value === 'string' ? /^(\d{1,2}):([0-5]\d)$/.exec(value) : null;
  const hour = time ? Number(time[1]) + Number(time[2]) / 60 : typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(hour) && hour >= 0 && hour <= 24 ? hour : fallback;
}

interface WorkTab { key: string; path: string; department: string; title: string; client: QueryClient; parent: string | null; context?: ChatContext | null; }
function workingClient(refreshDay: () => void): QueryClient {
  const client = new QueryClient({
    mutationCache: new MutationCache({ onSettled: () => {
      refreshDay();
      // Failed access checks also refresh this tab, so retained cache data
      // cannot keep presenting a revoked or erased record as actionable.
      void client.invalidateQueries();
    } }),
    defaultOptions: { queries: { retry: false, staleTime: 0 }, mutations: { retry: false } },
  });
  return client;
}
function pathOf(to: To): string {
  return typeof to === 'string' ? to : `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
}
function businessLocation(path: string): string | null {
  if (/^https?:\/\//i.test(path)) {
    let url: URL;
    try { url = new URL(path); } catch { return null; }
    if (url.origin !== window.location.origin) return null;
    path = `${url.pathname}${url.search}${url.hash}`;
  }
  return path.startsWith('/') && !path.startsWith('//') && matchRoutes(BUSINESS_ROUTES, path) !== null ? path : null;
}
export function businessPath(path: string): boolean {
  return businessLocation(path) !== null;
}

/** Each mounted tab owns its navigation and cache. No global department switch
 * or global mutable transport scope is used, including while another tab saves. */
function WorkView({ tab, active, open, back, changed, setContext, replacePath }: { tab: WorkTab; active: boolean; open: (path: string, department: string) => void; back: () => void; changed: () => void; setContext: (key: string, context: ChatContext | null) => void; replacePath: (key: string, path: string) => void }) {
  const me = useMeOrThrow();
  const navigation = useContext(UNSAFE_NavigationContext);
  const updateContext = useCallback((context: ChatContext | null) => setContext(tab.key, context), [tab.key, setContext]);
  const url = new URL(tab.path, 'https://workspace.invalid');
  const location: Location = { pathname: url.pathname, search: url.search, hash: url.hash, state: null, key: tab.key };
  const accessContext = { ...(tab.context ?? contextFor(url.pathname, tab.department)), department: tab.department };
  const access = useQuery({
    queryKey: ['today-record-access', me.humanId, tab.key, JSON.stringify(accessContext)], enabled: active, staleTime: 0, retry: false,
    queryFn: async () => {
      await post(appPath('/v1/today/record/call'), { scopeDepartment: tab.department, path: '/v1/chat/context', payload: { context: accessContext } });
      // An opened tab rechecks original readers, including provider-backed
      // document access, before exposing its retained controls and content.
      await tab.client.invalidateQueries();
      return true;
    },
  });
  const visible = active && access.isSuccess && !access.isFetching;
  const accessFailure = access.isError ? failureOf(access.error) : null;
  const accessLost = accessFailure !== null && (
    accessFailure.kind === 'unauthenticated' || accessFailure.kind === 'denied' || accessFailure.kind === 'not_found'
    || (accessFailure.kind === 'refused' && [401, 403, 404, 410].includes(accessFailure.status))
  );
  useEffect(() => {
    if (accessLost) tab.client.clear();
  }, [accessLost, tab.client]);
  const navigator = useMemo(() => {
    const move = (to: To) => {
      const path = businessLocation(pathOf(to));
      if (!path) return;
      if (new URL(path, 'https://workspace.invalid').pathname === url.pathname) replacePath(tab.key, path);
      else open(path, tab.department);
    };
    return {
    ...navigation.navigator,
    createHref: (to: To) => pathOf(to),
    push: move,
    replace: move,
    go: () => back(),
  }; }, [navigation.navigator, tab.department, tab.key, url.pathname, open, back, replacePath]);
  return <div hidden={!active}>
    {active && access.isFetching && <p role="status" className="p-4">Checking current record access…</p>}
    {active && access.isError && <div role="alert" className="p-4"><p>{said(failureOf(access.error))}</p><button className="underline min-h-11 min-w-11 px-2" onClick={() => void access.refetch()}>Retry opening this record</button></div>}
    <section hidden={!visible} aria-label={`Working record: ${tab.title}`} className="min-w-0" onChangeCapture={changed} onClickCapture={event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as Element).closest('a[href]');
    if (anchor?.getAttribute('target') === '_blank' || anchor?.hasAttribute('download')) return;
    const href = anchor?.getAttribute('href');
    if (href && businessPath(href)) { event.preventDefault(); event.stopPropagation(); open(href, tab.department); }
  }}>
    {!accessLost && <InlineForms active={visible}><QueryClientProvider client={tab.client}>
      <MeContext.Provider value={{ ...me, department: tab.department }}>
        <TodayCallScope department={tab.department}>
          <WorkingRecordProvider set={updateContext}>
          <EmbeddedFrame>
            <UNSAFE_NavigationContext.Provider value={{ ...navigation, navigator }}>
              <UNSAFE_LocationContext.Provider value={{ location, navigationType: NavigationType.Pop }}>
                <UNSAFE_RouteContext.Provider value={{ outlet: null, matches: [], isDataRoute: false }}>
                  <Suspense fallback={<p role="status">Loading the working record…</p>}><BusinessView /></Suspense>
                </UNSAFE_RouteContext.Provider>
              </UNSAFE_LocationContext.Provider>
            </UNSAFE_NavigationContext.Provider>
          </EmbeddedFrame>
          </WorkingRecordProvider>
        </TodayCallScope>
      </MeContext.Provider>
    </QueryClientProvider></InlineForms>}
  </section></div>;
}
function BusinessView() {
  const view = useRoutes(BUSINESS_ROUTES);
  return <><div className="border-b border-line p-2"><ManualAdd wide /></div>{view}</>;
}

export function TodayWorkspacePage() {
  const me = useMeOrThrow(), frame = useFrame(), outer = useQueryClient();
  const outerLocation = useLocation(), navigate = useNavigate();
  const workspaceElement = useRef<HTMLDivElement>(null);
  const [narrowWorkspace, setNarrowWorkspace] = useState(false);
  useEffect(() => {
    const element = workspaceElement.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width && width > 0) setNarrowWorkspace(width < 820);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const coordinatedViews = frame.layout?.phone || narrowWorkspace;
  const experience = useExperience(), preferences = experience.data?.resolved.preferences ?? {};
  const hours = { start: workingHour(preferences['dayStart'], 7), end: workingHour(preferences['dayEnd'], 19) };
  const [day, setDay] = useState(() => startOfDay(new Date()));
  const [pickedDay, setPickedDay] = useState(false);
  const [span, setSpan] = useState<ViewSpan>('day');
  const openingApplied = useRef(false), spanPicked = useRef(false);
  useEffect(() => {
    if (openingApplied.current || !experience.data) return;
    openingApplied.current = true;
    if (!spanPicked.current) setSpan(preferences['todaySpan'] === 'week' ? 'week' : preferences['todaySpan'] === 'three' ? 'next2' : 'day');
  }, [experience.data, preferences['todaySpan']]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [offset, setOffset] = useState(0), [tabs, setTabs] = useState<WorkTab[]>([]), [active, setActive] = useState<string | null>(null);
  const [department, setDepartment] = useState(me.department);
  const [dirty, setDirty] = useState<Set<string>>(() => new Set());
  const [closing, setClosing] = useState<string | null>(null);
  const [undo, setUndo] = useState<(() => Promise<void>) | null>(null);
  const [undoProblem, setUndoProblem] = useState<unknown>(null);
  const [phoneView, setPhoneView] = useState<'day' | 'work'>('day');
  const selectTab = useCallback((key: string | null, replace = false) => {
    setActive(key); setPhoneView(key ? 'work' : 'day');
    if (key !== active || replace) navigate('/today', { replace, state: { todayWorkspaceTab: key }, preventScrollReset: true });
  }, [active, navigate]);
  useEffect(() => {
    const key = (outerLocation.state as { todayWorkspaceTab?: unknown } | null)?.todayWorkspaceTab;
    const selected = typeof key === 'string' && tabs.some(tab => tab.key === key) ? key : null;
    setActive(selected); setPhoneView(selected ? 'work' : 'day');
    // History carries a selection only. Original readers still validate every
    // source; Back never destroys an unfinished form or grants another scope.
  }, [outerLocation.key]);
  useEffect(() => {
    if (!frame.workingDay) return;
    spanPicked.current = true;
    setDay(startOfDay(frame.workingDay.day)); setPickedDay(true); setSpan('day'); setOffset(0); setPhoneView('day');
  }, [frame.workingDay]);
  useEffect(() => {
    if (!dirty.size) return;
    const protect = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [dirty.size]);
  const q = useQuery<TodayWorkspace>({ queryKey: ['today-workspace', me.humanId, pickedDay ? localIsoDate(day) : null, span, offset, departmentFilter, kindFilter],
    queryFn: () => post(appPath('/v1/today/workspace'), { span, ...(pickedDay ? { date: localIsoDate(day) } : {}), offset,
      ...(departmentFilter !== 'all' ? { departmentFilter: departmentFilter === 'personal' ? null : departmentFilter } : {}),
      ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
    }), retry: false });
  useEffect(() => {
    if (pickedDay || !q.data || q.isFetching) return;
    const [year, month, date] = q.data.date.split('-').map(Number) as [number, number, number];
    setDay(new Date(year, month - 1, date));
  }, [q.data, q.isFetching, pickedDay]);
  const open = useCallback((path: string, scope: string, title?: string) => {
    const location = businessLocation(path);
    if (location === null) return;
    path = location;
    const target = new URL(path, 'https://workspace.invalid');
    const key = `${scope}:${target.pathname}`;
    setTabs(old => old.some(t => t.key === key) ? old.map(t => t.key === key && (target.search || target.hash) ? { ...t, path } : t) : [...old, { key, path, department: scope, parent: active, title: title ?? path.split('/').filter(Boolean)[0] ?? 'Work',
      client: workingClient(() => { void outer.invalidateQueries({ queryKey: ['today-workspace'] }); }) }]);
    selectTab(key);
  }, [active, outer, selectTab]);
  const chosen = tabs.find(t => t.key === active);
  const replacePath = useCallback((key: string, path: string) => setTabs(old => old.map(tab => tab.key === key ? { ...tab, path } : tab)), []);
  const setContext = useCallback((key: string, context: ChatContext | null) => setTabs(old => old.map(tab => tab.key === key && JSON.stringify(tab.context ?? null) !== JSON.stringify(context) ? { ...tab, context } : tab)), []);
  useEffect(() => {
    frame.setWorkspaceOpener?.((path, sourceDepartment) => {
      if (!businessPath(path)) return false;
      let scope = sourceDepartment ?? chosen?.department ?? department;
      if (sourceDepartment && !q.data?.departments.some(d => d.name === sourceDepartment)) {
        if (!/^\/projects\/[0-9a-f-]{36}(?:[?#]|$)/i.test(path)) return false;
        // The original project handler checks explicit membership. Sharing a
        // project never lends its home department or its other records.
        scope = chosen?.department ?? department;
      }
      open(path, scope);
      return true;
    });
    return () => frame.setWorkspaceOpener?.(null);
  }, [frame.setWorkspaceOpener, open, chosen?.department, department, q.data?.departments]);
  useEffect(() => {
    frame.detach();
    frame.setWorkspaceContext(chosen ? { ...(chosen.context ?? contextFor(chosen.path.split('?')[0]!, chosen.department)), department: chosen.department, workingDate: localIsoDate(day) } : { page: 'today', route: '/today', workingDate: localIsoDate(day) });
    return () => frame.setWorkspaceContext(null);
  }, [chosen?.key, chosen?.context, day, frame.setWorkspaceContext]);
  useEffect(() => {
    if (!q.data) return;
    const permitted = new Set(q.data.departments.map(d => d.name));
    if (!permitted.has(department) && q.data.departments[0]) setDepartment(q.data.departments[0].name);
    setTabs(old => old.filter(t => { if (permitted.has(t.department)) return true; t.client.clear(); return false; }));
  }, [q.data?.departments, department]);
  const close = (key: string, confirmed = false) => {
    const tab = tabs.find(t => t.key === key);
    if (!tab) return;
    if (dirty.has(key) && !confirmed) { setClosing(key); return; }
    // Closing is explicit; switching tabs never unmounts an unfinished form.
    tab.client.clear(); setTabs(old => old.filter(t => t.key !== key));
    setDirty(old => { const next = new Set(old); next.delete(key); return next; });
    setClosing(null);
    if (active === key) {
      const next = tabs.find(t => t.key === tab.parent)?.key ?? tabs.find(t => t.key !== key)?.key ?? null;
      selectTab(next, true);
    }
  };
  const openItem = (item: TodayWorkspaceItem) => { if (item.href) open(item.href, item.department ?? department, item.title); };
  return <div ref={workspaceElement} className="p-4 flex flex-col gap-4 min-w-0">
    <PageHeader kicker={longDate(day)} title={`Your day, ${me.displayName.split(' ')[0]}`} back={null} />
    {coordinatedViews && <nav aria-label="Today views" className="flex gap-4">
      <button className="underline min-h-11 min-w-11 px-2" aria-pressed={phoneView === 'day'} onClick={() => setPhoneView('day')}>Day</button>
      <button className="underline min-h-11 min-w-11 px-2" aria-pressed={phoneView === 'work'} onClick={() => setPhoneView('work')}>Work</button>
      <button className="underline min-h-11 min-w-11 px-2" onClick={() => frame.setChatOpen(true)}>Assistant</button>
    </nav>}
    <div hidden={coordinatedViews && phoneView !== 'day'} className="flex flex-wrap items-center gap-2">
      <button className="underline min-h-11 min-w-11 px-2" onClick={() => { setDay(addDays(day, -1)); setPickedDay(true); setOffset(0); }}>Earlier</button>
      <button aria-label="Go to today" className="underline min-h-11 min-w-11 px-2" onClick={() => { setPickedDay(false); setOffset(0); }}>Today</button>
      <button className="underline min-h-11 min-w-11 px-2" onClick={() => { setDay(addDays(day, 1)); setPickedDay(true); setOffset(0); }}>Later</button>
      <nav aria-label="Range" className="flex flex-wrap gap-3">{Object.entries(SPAN_LABEL).map(([key, label]) => <button className="underline min-h-11 min-w-11 px-2" key={key} aria-pressed={span === key} onClick={() => { spanPicked.current = true; setSpan(key as ViewSpan); setOffset(0); }}>{label}</button>)}</nav>
      {span === 'month' && <p>A month is shown as the next four weeks. This view remains proposed.</p>}
      <button className="underline min-h-11 min-w-11 px-2" onClick={() => frame.ask('Help me organise my day.', { page: 'today', route: '/today', workingDate: localIsoDate(day) })}>Ask {frame.twin ?? 'my assistant'}</button>
    </div>
    <details open={!coordinatedViews || phoneView === 'day'} className="min-w-0">
    <summary hidden={!coordinatedViews || phoneView === 'day'} className="min-h-11 min-w-11 px-2 underline cursor-pointer">Open another workflow</summary>
    <div className="flex flex-wrap gap-3 items-center" aria-label="Open business work">
      <label>Open new work in <Select value={department} onChange={e => setDepartment(e.target.value)}>{(q.data?.departments ?? []).map(d => <option key={d.name} value={d.name}>{d.label}</option>)}</Select></label>
      <button className="underline min-h-11 min-w-11 px-2" onClick={() => open('/today/department', department, 'Department day')}>Department day</button>
      {Object.entries({ Calendar: '/calendar', Tasks: '/tasks', Mail: '/mail', Documents: '/documents' }).map(([label, path]) => <button key={path} className="underline min-h-11 min-w-11 px-2" onClick={() => open(path, department, label)}>{label}</button>)}
      <label>More work <Select value="" onChange={event => { const path = event.target.value; if (path) open(path, department, event.target.selectedOptions[0]?.text); }}><option value="">Open a workflow</option>{Object.entries({ Companies: '/companies', Contacts: '/contacts', Projects: '/projects', Requests: '/requests', Approvals: '/approvals', Institutions: '/institutions', Prospecting: '/prospecting/plans', Registers: '/registers', Reports: '/reports', Notifications: '/notifications' }).map(([label, path]) => <option key={path} value={path}>{label}</option>)}</Select></label>
    </div>
    </details>
    <div className={`grid gap-4 min-w-0 ${coordinatedViews ? 'grid-cols-1' : 'grid-cols-[minmax(220px,240px)_minmax(0,1fr)]'}`}>
      <section hidden={coordinatedViews && phoneView !== 'day'} aria-label="Your day across departments" className="min-w-0 space-y-3">
        <h2 className="font-semibold">Day and work queue</h2>
        <Covering />
        <div className="flex flex-wrap gap-3">
          <label>Queue department <Select value={departmentFilter} onChange={event => { setDepartmentFilter(event.target.value); setOffset(0); }}><option value="all">All permitted departments</option><option value="personal">Personal</option>{q.data?.departments.map(d => <option key={d.name} value={d.name}>{d.label}</option>)}</Select></label>
          <label>Queue kind <Select value={kindFilter} onChange={event => { setKindFilter(event.target.value); setOffset(0); }}><option value="all">All work</option>{Object.entries({ task: 'Tasks', calendar_event: 'Meetings', reply_needed: 'Replies', chase: 'Follow-ups', brief: 'Briefs', shared_inbox: 'Shared inbox', merge_proposal: 'Merge reviews', break: 'Breaks', note: 'Notes' }).map(([kind, label]) => <option key={kind} value={kind}>{label}</option>)}</Select></label>
        </div>
        {q.data && q.data.departments.some(d => d.name === department) && <DayRecap department={department} label={q.data.departments.find(d => d.name === department)!.label} date={localIsoDate(day)} zone={q.data.zone} open={open} />}
        {q.data && span === 'day' && <FreeHours workspace={q.data} hours={hours} open={path => open(path, department, 'Calendar')} />}
        {undo && <button className="underline min-h-11 min-w-11 px-2" onClick={() => { setUndoProblem(null); void undo().then(() => setUndo(null)).catch(setUndoProblem); }}>Undo last day change</button>}
        {undoProblem !== null && <p role="alert">{said(failureOf(undoProblem))}</p>}
        {q.isPending ? <p role="status">Loading your permitted work…</p> : q.isError ? <p role="alert">{said(failureOf(q.error))}</p> : <>
          <p>{q.data.total} items · {q.data.zone}</p>
          {q.data.unavailable > 0 && <p role="status">Some source content is unavailable. Its original access and erasure rules still apply; no substitute action is shown.</p>}
          {!q.data.total && <p>Nothing on this day. Create a task or open business work above.</p>}
          <ol className="space-y-2">{q.data.items.map(item => <li key={item.key} data-testid={`item-${item.kind}`} className="border border-line p-3 space-y-1">
            <div className="text-meta text-label">{item.at ? new Intl.DateTimeFormat('en-GB', { timeZone: q.data.zone, hour: '2-digit', minute: '2-digit', timeZoneName: 'shortOffset' }).format(new Date(item.at)) : 'Whenever'} · {item.department ? q.data.departments.find(d => d.name === item.department)?.label ?? item.department : 'Personal'}</div>
            {item.href ? <button className="text-left underline min-h-11 min-w-11 px-2" onClick={() => openItem(item)}>{item.title}</button> : <p>{item.title}</p>}
            <p>{item.state.replaceAll('_', ' ')}{item.pinned ? ' · Pinned' : ''}</p>
            {item.placedBy === 'agent' && <p data-testid="provenance">{item.actorName ?? 'An agent'} placed it: {item.reason ?? 'No reason recorded'}.{item.origin ? ` Started by ${STARTED_BY[item.origin]}.` : ''}</p>}
            {item.request && <div className="space-y-1"><p>{q.data.departments.find(d => d.name === item.request!.fromDepartment)?.label ?? item.request.fromDepartment.replaceAll('_', ' ')} asked {q.data.departments.find(d => d.name === item.request!.toDepartment)?.label ?? item.request.toDepartment.replaceAll('_', ' ')} · {item.request.state.replaceAll('_', ' ')} · with the {item.request.routedTo}.</p><button className="underline min-h-11 min-w-11 px-2" onClick={() => open(item.request!.href, item.department ?? department, 'Request')}>{['done', 'refused', 'cancelled'].includes(item.request.state) ? 'Review request' : 'Answer the request'}</button></div>}
            <DayActions item={item} scopeDepartment={item.department ?? department} day={day} onUndo={action => setUndo(() => action)} />
          </li>)}</ol>
          <div className="flex gap-3">{offset > 0 && <button className="underline min-h-11 min-w-11 px-2" onClick={() => setOffset(Math.max(0, offset - 50))}>Previous items</button>}{q.data.next !== null && <button className="underline min-h-11 min-w-11 px-2" onClick={() => setOffset(q.data.next!)}>More items</button>}</div>
        </>}
      </section>
      <section hidden={coordinatedViews && phoneView !== 'work'} aria-label="Working panel" className="min-w-0 border border-line">
        {closing && <div role="alertdialog" aria-label="Close edited work" className="p-4 space-y-3"><p>This tab contains edited fields. Keep it open to retain them, or close it and discard any unsaved edits.</p><button className="underline min-h-11 min-w-11 px-2 mr-4" onClick={() => setClosing(null)}>Keep working</button><button className="underline min-h-11 min-w-11 px-2" onClick={() => close(closing, true)}>Close and discard unsaved edits</button></div>}
        <div role="tablist" aria-label="Open work" className="flex flex-wrap gap-2 border-b border-line p-2">{tabs.map(t => <div key={t.key} className="flex gap-1"><button role="tab" aria-selected={t.key === active} onClick={() => selectTab(t.key)} className="min-h-11 min-w-11 px-2 underline">{t.title}</button><button className="min-h-11 min-w-11 px-2" aria-label={`Close ${t.title}`} onClick={() => close(t.key)}>×</button></div>)}</div>
        {!tabs.length && <p className="p-6">Open a day item or business workflow. Its full controls appear here; your day stays in place.</p>}
        {chosen && <p aria-label="Working record department" className="px-3 pt-3 text-label text-meta">Working in {q.data?.departments.find(d => d.name === chosen.department)?.label ?? chosen.department}</p>}
        {chosen && <button className="underline min-h-11 min-w-11 px-2 p-2" onClick={() => chosen.parent && tabs.some(t => t.key === chosen.parent) ? selectTab(chosen.parent) : close(chosen.key)}>Back to {chosen.parent ? 'parent work' : 'day'}</button>}
        {tabs.map(t => <WorkView key={t.key} tab={t} active={t.key === active} open={open} setContext={setContext} replacePath={replacePath} changed={() => setDirty(old => new Set(old).add(t.key))} back={() => t.parent && tabs.some(other => other.key === t.parent) ? selectTab(t.parent) : close(t.key)} />)}
      </section>
    </div>
  </div>;
}
